// "Data access" on the project page of a HOSTED project: each member's data role (reader /
// editor / admin), the editor scope (an editor writes their own tables / every table) and, per
// table, who owns it and whether the signed-in user may change its rows. GET/PATCH
// /api/projects/{id}/data-access; only a data admin (the project's curators, admins, a member
// with data role admin) sees the controls. The backend enforces the same in PostgreSQL. A
// curator is always a data admin and reads personal data: their row is read-only here (the
// server answers 422 otherwise). While a migration runs (hosting_mode "maintenance") a banner
// says so; changes then answer 423.
// Review before commit (task 2026-10-05 "imports can require a data steward's approval"): a data
// admin switches `import_review` on and flags members as data stewards; a steward (or a data
// admin) then approves or rejects the imports others submit (ImportReviewPanel on the Data page).
// "Reads personal data" (per member, data admins only) lets the member read the columns marked
// personal / sensitive unmasked; the panel names those columns and how the caller sees them.
import { useState } from "react";
import { api } from "../api";
import { ErrorNote, Loading, SectionTitle } from "../components/Feedback";
import { Badge, Card, Field, Select, Toggle } from "../components/Shared";
import { useApi } from "../useApi";

const ROLE_OPTIONS = [
  { value: "reader", label: "reader — reads every table" },
  { value: "editor", label: "editor — changes rows (see editor scope)" },
  { value: "admin", label: "admin — changes every table and this panel" },
];
const SCOPE_OPTIONS = [
  { value: "own", label: "Editors write their own tables" },
  { value: "all_tables", label: "Editors write every table (hub tables stay with data admins)" },
];

const th = { textAlign: "left", padding: "6px 8px 6px 0", fontSize: 12, fontWeight: 600, color: "#4b5563" };
const td = { padding: "6px 8px 6px 0", verticalAlign: "middle" };
const personName = m => m.full_name ? `${m.full_name} (${m.username})` : m.username;

function MemberRow({ m, canAdmin, saving, patch }) {
  const curator = m.project_role === "curator";
  const editable = canAdmin && !curator;
  return (
    <tr data-member={m.username} style={{ borderTop: "1px solid #e5e7eb" }}>
      <th scope="row" style={{ ...td, fontWeight: 600, textAlign: "left" }}>{personName(m)}</th>
      <td data-label="Project role" style={td}>{m.project_role}</td>
      <td data-label="Data role" style={td}>
        {editable ? (
          <Select aria-label={`Data role of ${m.username}`} options={ROLE_OPTIONS} value={m.role} disabled={saving}
                  onChange={e => e.target.value && patch({ members: [{ user_id: m.user_id, role: e.target.value }] })} />
        ) : (
          <Badge color={m.role === "reader" ? "blue" : "green"}>{m.role}</Badge>
        )}
        {canAdmin && curator && <span style={{ display: "block", fontSize: 11, color: "#6b7280" }}>a curator is always a data admin</span>}
      </td>
      <td data-label="Data steward" style={td}>
        {canAdmin ? (
          // The label is the tap target on a phone (a bare checkbox is 13 px).
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={!!m.steward} disabled={saving}
                   aria-label={`${m.username} is a data steward`}
                   onChange={e => patch({ members: [{ user_id: m.user_id, steward: e.target.checked }] })} />
          </label>
        ) : (m.steward ? "yes" : "no")}
      </td>
      <td data-label="Reads personal data" style={td}>
        {editable ? (
          <Toggle checked={!!m.personal_data} label={`${m.username} reads personal data`} disabled={saving}
                  onChange={v => patch({ members: [{ user_id: m.user_id, personal_data: v }] })} />
        ) : (m.personal_data ? "yes" : "no")}
      </td>
    </tr>
  );
}

export default function DataAccessPanel({ projectId }) {
  const access = useApi(`/projects/${projectId}/data-access`);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const d = access.data;

  const patch = async body => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/data-access`, body);
      access.reload();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-label="Data access" style={{ marginBottom: 24 }}>
      <SectionTitle>Data access</SectionTitle>
      <ErrorNote error={access.error || error} />
      {access.loading && !d && <Loading />}
      {d && (
        <Card style={{ padding: "18px 22px" }}>
          {d.hosting_mode === "maintenance" && (
            <div role="status" data-testid="maintenance-banner" style={{ margin: "0 0 12px", padding: "10px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.35)", color: "#92400e", fontSize: 13 }}>
              <strong>{d.database_name} is in maintenance:</strong> a migration to a new design version is
              running. Reading works; changes are refused until it ends.
            </div>
          )}
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#374151" }}>
            Your data role on <code>{d.database_name}</code>: <Badge color="blue">{d.my_level}</Badge>
            {d.is_steward && <> <Badge color="green">data steward</Badge></>}
          </p>
          {(d.marked_columns || []).length > 0 && (
            <p data-testid="personal-data-note" style={{ margin: "0 0 12px", fontSize: 13, color: "#374151" }}>
              Personal data: {d.marked_columns.map(m => `${m.table}.${m.column}`).join(", ")} —{" "}
              {d.my_personal_data
                ? "you read these columns unmasked; every read is logged."
                : <>masked (***) for you, in SQL too (read <code>{d.pg_roles?.masked_schema || "unio_masked"}</code>.&lt;table&gt;).</>}
            </p>
          )}
          <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <caption style={{ textAlign: "left", fontWeight: 600, padding: "4px 0" }}>Members' data roles</caption>
            <thead>
              <tr>
                <th scope="col" style={th}>Member</th><th scope="col" style={th}>Project role</th>
                <th scope="col" style={th}>Data role</th><th scope="col" style={th}>Data steward</th>
                <th scope="col" style={th}>Reads personal data</th>
              </tr>
            </thead>
            <tbody>
              {d.members.map(m => <MemberRow key={m.user_id} m={m} canAdmin={d.can_admin} saving={saving} patch={patch} />)}
            </tbody>
          </table>
          <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
            {d.can_admin ? (
              <Field label="Editor scope" hint="Which tables a member with the data role editor may change.">
                <Select options={SCOPE_OPTIONS} value={d.editor_scope} disabled={saving}
                        onChange={e => e.target.value && patch({ editor_scope: e.target.value })} />
              </Field>
            ) : (
              <div style={{ fontSize: 14 }}>
                Editor scope: {SCOPE_OPTIONS.find(o => o.value === d.editor_scope)?.label || d.editor_scope}
              </div>
            )}
            {d.can_admin ? (
              <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 14 }}>
                <Toggle checked={!!d.import_review} disabled={saving} label="Review imports before they are written"
                        onChange={v => patch({ import_review: v })} />
                <span>Review imports before they are written: a data steward or a data admin other than
                  the uploader approves each file import.</span>
              </div>
            ) : (
              <div style={{ fontSize: 14 }}>
                Imports are {d.import_review ? "reviewed by a data steward before they are written" : "written when their uploader commits them"}.
                {d.import_review && !d.members.some(m => m.steward) && " There is no data steward: data admins review."}
              </div>
            )}
          </div>
          {d.tables.length > 0 && (
            <table className="responsive-table" style={{ marginTop: 16, width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <caption style={{ textAlign: "left", fontWeight: 600, padding: "4px 0" }}>Tables</caption>
              <thead>
                <tr style={{ textAlign: "left", color: "#6b7280" }}>
                  <th scope="col" style={th}>Table</th><th scope="col" style={th}>Owner</th>
                  <th scope="col" style={th}>You may change rows</th>
                </tr>
              </thead>
              <tbody>
                {d.tables.map(t => (
                  <tr key={t.name} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td data-label="Table" style={{ fontFamily: "'JetBrains Mono', monospace", padding: "4px 0" }}>{t.name}</td>
                    <td data-label="Owner">{t.kind === "hub" ? "shared entity (hub)" : t.owner || "—"}</td>
                    <td data-label="You may change rows">{t.can_write ? "yes" : "no"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </section>
  );
}
